import { useEffect, useMemo, useState } from "react";
import { CheckSquare, XSquare, Archive } from "lucide-react";
import { adminGetBookings, adminBulkBookingStatus, adminSetBookingStatus, getBookingSettings, adminSetBookingSettings } from "@/lib/api";
import { toast } from "sonner";

const TABS = [
  { id: "all", label: "All bookings" },
  { id: "free_consult", label: "Free consult" },
  { id: "paid_consult", label: "Paid consult" },
  { id: "day", label: "Day pass" },
  { id: "exclusive", label: "Exclusive pass" },
  { id: "overnight", label: "Overnight pass" },
];

const STATUS_CLS = {
  new: "bg-[#FFB56B]/20 text-[#9A5A12]",
  approved: "bg-[#D7E85C]/30 text-[#5C6B12]",
  declined: "bg-[#FF8473]/20 text-[#B3402F]",
  archived: "bg-[#1F3864]/10 text-[#1F3864]/50",
};
const CAT_LABEL = { free_consult: "FREE CONSULT", paid_consult: "PAID CONSULT", day: "DAY PASS", exclusive: "EXCLUSIVE PASS", overnight: "OVERNIGHT PASS" };

const iso = (d) => d.toISOString().slice(0, 10);

export default function BookingsAdmin() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("all");
  const [range, setRange] = useState("all"); // all | today | week | month | custom
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [leadDays, setLeadDays] = useState(2);
  const [savingLead, setSavingLead] = useState(false);

  useEffect(() => {
    getBookingSettings().then((s) => setLeadDays(s.lead_time_days ?? 2)).catch(() => {});
  }, []);

  const saveLead = async () => {
    setSavingLead(true);
    try {
      const r = await adminSetBookingSettings(Number(leadDays));
      setLeadDays(r.lead_time_days);
      toast.success(`Lead time set to ${r.lead_time_days} day(s)`);
    } catch { toast.error("Could not save lead time"); }
    setSavingLead(false);
  };

  const dates = useMemo(() => {
    const now = new Date();
    if (range === "new_today") return { created_today: true }; // booked today, any booking date
    if (range === "week") {
      // current calendar week (Mon–Sun)
      const s = new Date(now);
      s.setDate(now.getDate() - ((now.getDay() + 6) % 7));
      const e = new Date(s);
      e.setDate(s.getDate() + 6);
      return { date_from: iso(s), date_to: iso(e) };
    }
    if (range === "month") {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      const e = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { date_from: iso(s), date_to: iso(e) };
    }
    if (range === "custom") return { date_from: from || undefined, date_to: to || undefined };
    return {};
  }, [range, from, to]);

  const load = () => {
    adminGetBookings({ category: tab === "all" ? undefined : tab, ...dates })
      .then(setRows).catch(() => toast.error("Failed to load bookings"));
  };
  useEffect(() => { setSelected(new Set()); load(); /* eslint-disable-next-line */ }, [tab, range, from, to]);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => (s.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))));

  const bulk = async (status) => {
    if (selected.size === 0) return;
    try {
      const r = await adminBulkBookingStatus([...selected], status);
      toast.success(`${r.updated} booking(s) marked ${status}`);
      setSelected(new Set());
      load();
    } catch { toast.error("Update failed"); }
  };

  const setOne = async (id, status) => {
    try { await adminSetBookingStatus(id, status); toast.success(`Marked ${status}`); load(); }
    catch { toast.error("Update failed"); }
  };

  return (
    <div data-testid="admin-bookings">
      <h1 className="font-display font-bold text-3xl text-[#1F3864] mb-1">Bookings</h1>
      <p className="text-[#1F3864]/60 mb-6">Consultations and studio passes — review, approve, decline or archive.</p>

      {/* availability settings */}
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-2xl border border-[#1F3864]/10 p-4 mb-4" data-testid="booking-settings">
        <div>
          <p className="text-sm font-bold text-[#1F3864]">Booking lead time</p>
          <p className="text-xs text-[#1F3864]/50">New bookings must be at least this many days ahead (PHT). Weekends stay open for Overnight Pass only.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <input type="number" min="0" max="30" data-testid="lead-time-input" value={leadDays}
            onChange={(e) => setLeadDays(e.target.value)}
            className="w-20 border border-[#1F3864]/20 rounded-lg px-3 py-2 text-sm bg-white font-bold text-[#1F3864]" />
          <span className="text-sm text-[#1F3864]/60">day(s)</span>
          <button data-testid="lead-time-save" onClick={saveLead} disabled={savingLead}
            className="bg-[#1F3864] text-[#D7E85C] font-bold text-sm px-4 py-2 rounded-full hover:bg-[#16294B] disabled:opacity-50">
            {savingLead ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* category tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`booking-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-full text-sm font-bold ${tab === t.id ? "bg-[#1F3864] text-[#D7E85C]" : "bg-white text-[#1F3864] border border-[#1F3864]/20"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* date range + bulk actions */}
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-2xl border border-[#1F3864]/10 p-4 mb-6">
        <select data-testid="booking-range" value={range} onChange={(e) => setRange(e.target.value)}
          className="border border-[#1F3864]/20 rounded-lg px-3 py-2 text-sm bg-white font-bold text-[#1F3864]">
          <option value="all">All time</option>
          <option value="new_today">New Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="custom">Custom range</option>
        </select>
        {range === "custom" && (
          <>
            <input type="date" data-testid="booking-from" value={from} onChange={(e) => setFrom(e.target.value)}
              className="border border-[#1F3864]/20 rounded-lg px-3 py-2 text-sm bg-white" />
            <span className="text-[#1F3864]/50 text-sm">to</span>
            <input type="date" data-testid="booking-to" value={to} onChange={(e) => setTo(e.target.value)}
              className="border border-[#1F3864]/20 rounded-lg px-3 py-2 text-sm bg-white" />
          </>
        )}
        <span className="ml-auto text-sm font-bold text-[#1F3864]">{selected.size} selected</span>
        <button data-testid="bulk-approve" onClick={() => bulk("approved")} disabled={selected.size === 0}
          className="inline-flex items-center gap-1.5 bg-[#D7E85C] text-[#1F3864] font-bold text-sm px-4 py-2 rounded-full disabled:opacity-40">
          <CheckSquare size={15} /> Approve
        </button>
        <button data-testid="bulk-decline" onClick={() => bulk("declined")} disabled={selected.size === 0}
          className="inline-flex items-center gap-1.5 bg-[#FF8473]/20 text-[#B3402F] font-bold text-sm px-4 py-2 rounded-full disabled:opacity-40">
          <XSquare size={15} /> Decline
        </button>
        <button data-testid="bulk-archive" onClick={() => bulk("archived")} disabled={selected.size === 0}
          className="inline-flex items-center gap-1.5 bg-[#1F3864]/10 text-[#1F3864] font-bold text-sm px-4 py-2 rounded-full disabled:opacity-40">
          <Archive size={15} /> Archive
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-[#1F3864]/10 overflow-x-auto">
        {rows.length === 0 ? (
          <p data-testid="bookings-empty" className="text-center text-[#1F3864]/50 text-sm py-12">No bookings match this filter.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-[#1F3864]/50 border-b border-[#1F3864]/10">
                <th className="px-4 py-3 w-8">
                  <input type="checkbox" data-testid="booking-select-all" checked={rows.length > 0 && selected.size === rows.length} onChange={toggleAll} className="accent-[#1F3864] h-4 w-4" />
                </th>
                <th className="px-4 py-3">Name</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Slot</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1F3864]/5">
              {rows.map((b) => (
                <tr key={b.id} data-testid={`booking-row-${b.id}`}>
                  <td className="px-4 py-3"><input type="checkbox" data-testid={`booking-check-${b.id}`} checked={selected.has(b.id)} onChange={() => toggle(b.id)} className="accent-[#1F3864] h-4 w-4" /></td>
                  <td className="px-4 py-3"><div className="font-bold text-[#1F3864]">{b.name}</div><div className="text-xs text-[#1F3864]/50">{b.email}</div></td>
                  <td className="px-4 py-3">{CAT_LABEL[b.category] || b.type}{b.guests ? ` · ${b.guests} guest(s)` : ""}</td>
                  <td className="px-4 py-3">{b.date}</td>
                  <td className="px-4 py-3">{b.time || b.session || "—"}</td>
                  <td className="px-4 py-3 font-bold">{b.amount ? `${b.currency === "php" ? "₱" : "$"}${b.amount}` : "Free"}</td>
                  <td className="px-4 py-3">
                    <select data-testid={`booking-status-${b.id}`} value={b.status || "new"} onChange={(e) => setOne(b.id, e.target.value)}
                      className={`text-xs font-bold rounded-full px-3 py-1.5 border-0 cursor-pointer ${STATUS_CLS[b.status || "new"]}`}>
                      <option value="new">New</option><option value="approved">Approved</option>
                      <option value="declined">Declined</option><option value="archived">Archived</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
